import type { TFunction } from 'i18next';

/**
 * What the Jira provisioning did, reported next to a creation that **succeeded** — the account
 * exists either way, which is why this cannot travel as a rejected promise. `accountId: null` is
 * no portal account at all, a non-empty `failedDesks` a half-provisioned one. The desks arrive
 * already named by the API, which is where the desk config lives.
 */
export interface JiraProvisioningMeta {
  accountId: string | null;
  /** Jira account type; `'atlassian'` is a refusal no retry fixes, and reads as a fact, not a fault. */
  accountType: string | null;
  failedDesks: string[];
}

/** `meta.jira`, narrowed. Anything else reads as "nothing to say" rather than throwing. */
export const jiraProvisioningOf = (meta: unknown): JiraProvisioningMeta | null => {
  const jira = (meta as { jira?: unknown } | null)?.jira as JiraProvisioningMeta | undefined;
  if (!jira || typeof jira !== 'object') return null;
  return {
    accountId: typeof jira.accountId === 'string' ? jira.accountId : null,
    accountType: typeof jira.accountType === 'string' ? jira.accountType : null,
    failedDesks: Array.isArray(jira.failedDesks) ? jira.failedDesks.map(String) : [],
  };
};

/**
 * What to tell an operator after a creation that worked, about the Jira half that may not have.
 *
 * Never « cannot receive requests »: a technician Jira turned away is still sent his request by
 * e-mail and can still accept it — only his place in the ticket's participants is missing.
 */
export const provisioningNotice = (
  t: TFunction,
  name: string,
  jira: JiraProvisioningMeta | null
): { tone: 'success' | 'error'; message: string } => {
  if (jira && jira.failedDesks.length > 0) {
    // « Jira refused » sends the operator hunting a breakdown. An Atlassian account is not one:
    // the call answers 204 and adds nobody. It is not a dead end either — an admin adding the
    // account to the project's customers works, and leaves its type `atlassian` (A2).
    const vars = { name, desks: jira.failedDesks.join(', ') };
    const message =
      jira.accountType === 'atlassian'
        ? t('{{name}} was created, but their Jira account is an Atlassian one, which GPS cannot add to {{desks}} automatically. An administrator can add them to the project customers in Jira. They still receive their requests by e-mail.', vars)
        : t('{{name}} was created, but Jira refused to add them to {{desks}}. They still receive their requests by e-mail.', vars);
    return { tone: 'error', message };
  }
  if (jira && jira.accountId === null) {
    return {
      tone: 'error',
      message: t('{{name}} was created, but no Jira account could be set up. They still receive their requests by e-mail.', { name }),
    };
  }
  return { tone: 'success', message: t('{{name}} created', { name }) };
};
